"use client";
import ApiLoader from "@/components/common/ApiLoader";
import PerfectMatch from "@/components/tools/checkEligibility/PerfectMatches";
import Footer from "@/components/common/Footer";
import { useAppSelector } from "@/store/hooks";
import { University } from "@/store/types/universityTypes";
import { checkCountry, replacedName } from "@/utils/Constants";
import { getFormattedCountryName } from "@/utils/getFormattedCountryName";
import { CircleAlert, Clock, IndianRupee, Plane } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import NewBanner from "./NewBanner";
import { NewNavBar } from "@/components/common/NewNavBar";

import { motion, Variants } from "framer-motion";
import NewAbout from "./NewAbout";
import Gallery from "./Gallery";
import EntryRequirements from "./EntryRequirements";
import Strengths from "./Strengths";
import LivingCost from "./LivingCost";
import OpenIntakes from "./OpenIntakes";
import Programs from "./Programs";
import TalkToExperts from "../homepage/TalkToExperts";
import { sendCustomGaEvent } from "@/scripts/ga";
import { useAuth } from "@/hook/useAuth";
import Link from "next/link";
import HowToApply from "../course/HowToApply";

export const getCountryByIdQuery = `
  *[_type == "countryData" && id == $id][0]{
    id,
    countryName,
    currency,
    description,

    "bgImg": bgImg.asset->url,

    popularFields[]{
      title,
      description,
      icon
    },

    "pathway": pathway.asset->url,
    "mobilePathway": mobilePathway.asset->url,

    chartData{
      lowestFee,
      highestFee
    },

    earnWhileStudy,

    slides[]{
      title,
      description,
      "image": image.asset->url
    }
  }
`;

type PostWorkPermitCountry =
  | "united kingdom"
  | "canada"
  | "australia"
  | "united states of america"
  | "malta"
  | "new zealand"
  | "ireland"
  | "netherlands"
  | "germany"
  | "france"
  | "spain"
  | "singapore"
  | "cyprus";

const TABS = [
  { id: "about", label: "About" },
  { id: "gallery", label: "Gallery" },
  { id: "strengths", label: "Strengths" },

  { id: "entry-requirements", label: "Entry requirements" },
  { id: "open-intakes", label: "Open Intakes" },
  { id: "programs", label: "Programs" },
  { id: "how-to-apply", label: "How to Apply" },
];

// COMPONENTS -------------------------------------------------------
const fadeUp: Variants = {
  hidden: {
    opacity: 0,
    y: 30,
  },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.6,
      ease: "easeOut",
    },
  }),
};

// ─── Section wrapper ──────────────────────────────────────────
function Section({
  id,
  title,
  children,
  sectionRef,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
  sectionRef: (el: HTMLElement | null) => void;
}) {
  return (
    <motion.section
      id={id}
      ref={sectionRef}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-12% 0px -12% 0px" }}
      variants={fadeUp}
      className="scroll-mt-14 py-1"
    >
      {children}
    </motion.section>
  );
}

const UniversityDetail = ({
  universityData,
}: {
  universityData: University;
}) => {
  const { isAnonymous } = useAuth();

  const [expertForm, setExpertForm] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [loadingState, setLoadingState] = useState(true);

  const { loading } = useAppSelector((state) => state.loader);

  const SECTION_CONTENT: Record<string, React.ReactNode> = {
    about: <NewAbout universityData={universityData} />,
    gallery: <Gallery data={universityData} />,
    strengths: <Strengths data={universityData} />,

    "entry-requirements": (
      <EntryRequirements universityDetail={universityData} />
    ),
    "open-intakes": <OpenIntakes universityDetail={universityData} />,
    programs: <Programs universityDetail={universityData} />,
    "how-to-apply": <HowToApply />,
  };

  const [activeTab, setActiveTab] = useState("overview");
  const sectionEls = useRef<Record<string, HTMLElement>>({});
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const registerRef = useCallback(
    (id: string) => (el: HTMLElement | null) => {
      if (el) {
        sectionEls.current[id] = el;
      } else {
        delete sectionEls.current[id];
      }
    },
    [],
  );

  // Auto-scroll active tab button into view horizontally without disturbing window vertical scroll
  useEffect(() => {
    if (!activeTab) return;
    const tabEl = tabRefs.current[activeTab];
    const container = tabsContainerRef.current;
    if (!tabEl || !container) return;

    const containerRect = container.getBoundingClientRect();
    const tabRect = tabEl.getBoundingClientRect();

    if (tabRect.left < containerRect.left || tabRect.right > containerRect.right) {
      const scrollLeftTarget =
        tabEl.offsetLeft - container.offsetWidth / 2 + tabEl.offsetWidth / 2;
      container.scrollTo({
        left: scrollLeftTarget,
        behavior: "smooth",
      });
    }
  }, [activeTab]);

  useEffect(() => {
    if (loadingState) return;

    const observers: IntersectionObserver[] = [];
    const intersecting = new Set<string>();

    const updateActive = () => {
      if (isScrollingRef.current) return;
      let activeId = "";
      let minDistance = Infinity;

      for (const tab of TABS) {
        if (intersecting.has(tab.id)) {
          const el = sectionEls.current[tab.id];
          if (el) {
            const rect = el.getBoundingClientRect();
            // Calculate distance from top of viewport (offset by 100px for header/tabs)
            const distance = Math.abs(rect.top - 100);
            if (distance < minDistance) {
              minDistance = distance;
              activeId = tab.id;
            }
          }
        }
      }

      if (activeId) {
        setActiveTab(activeId);
      }
    };

    Object.entries(sectionEls.current).forEach(([id, el]) => {
      if (!el) return;
      const obs = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) =>
            e.isIntersecting ? intersecting.add(id) : intersecting.delete(id),
          );
          updateActive();
        },
        { rootMargin: "-20% 0px -60% 0px", threshold: 0 },
      );
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [loadingState]);

  const handleTabClick = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      isScrollingRef.current = true;
      setActiveTab(id);
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - 56,
        behavior: "smooth",
      });

      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 800);
    }
  };

  useEffect(() => {
    setTimeout(() => {
      setLoadingState(false);
    }, 2000);
  }, []);

  const addressInfo = universityData?.addressInfo;

  const postWorkPermitDuration = universityData?.postWorkPermitDuration;

  const expertFromHandler = () => {
    void sendCustomGaEvent("university_talk_to_experts_open_click", {
      is_anonymous: isAnonymous,
    });
    setExpertForm(true);
  };

  useEffect(() => {
    setCurrentStep(isAnonymous ? 1 : 2);
  }, [expertForm, isAnonymous]);

  const normalizeYear = (year: number): number => {
    if (!Number.isInteger(year)) return year;
    return year >= 0 && year <= 99 ? 2000 + year : year;
  };

  const createSafeDate = (
    year: number,
    month: number,
    day: number,
  ): Date | null => {
    const date = new Date(year, month - 1, day);
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }
    return date;
  };

  const parseDateWithFallback = (dateStr: string): Date | null => {
    if (!dateStr || typeof dateStr !== "string") return null;

    const parts = dateStr.split("/").map((value) => parseInt(value.trim(), 10));
    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
      return null;
    }

    const [firstPart, secondPart, rawYearPart] = parts;
    const yearPart = normalizeYear(rawYearPart);

    // Prefer DD/MM/YYYY; fallback to MM/DD/YYYY for inconsistent data.
    const preferredDate = createSafeDate(yearPart, secondPart, firstPart);
    if (preferredDate) {
      return preferredDate;
    }

    const fallbackDate = createSafeDate(yearPart, firstPart, secondPart);
    if (fallbackDate) {
      return fallbackDate;
    }

    return null;
  };
  const getOrdinalSuffix = (day: number): string => {
    if (day > 3 && day < 21) return "th";

    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const formatDateWithOrdinal = (dateStr: string): string => {
    try {
      const parsedDate = parseDateWithFallback(dateStr);

      if (!parsedDate || isNaN(parsedDate.getTime())) {
        throw new Error("Invalid date");
      }

      const day = parsedDate.getDate();
      const month = parsedDate.toLocaleString("en-US", {
        month: "long",
      });
      const year = parsedDate.getFullYear();

      return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
    } catch (error) {
      console.error("formatDateWithOrdinal error:", (error as Error).message);
      return "Invalid date";
    }
  };

  const uniqueOpenIntakes = Array.isArray(universityData.intakeDetails)
    ? Array.from(
      new Map(
        universityData.intakeDetails
          .flatMap((item, index) => {
            const {
              month = [],
              status = [],
              deadline = [],
            } = item?.intake_details || {};

            const openIndexes = status
              .map((s: string, i: number) =>
                s?.toLowerCase() === "open" ? i : -1,
              )
              .filter((i: number) => i !== -1);

            return openIndexes.map((openIndex: number) => ({
              key: `${month[openIndex]}-${deadline[openIndex]}`,
              month: month[openIndex],
              deadline: deadline[openIndex],
              status: status[openIndex],
              originalIndex: index,
            }));
          })
          .map((item) => [item.key, item]),
      ).values(),
    )
    : [];

  return (
    <div className="bg-[#F7F5FF]">
      <NewNavBar />
      <NewBanner universityDetail={universityData} />
      <div className="bg-[#E5F0FF]">
        <div className="container 2xl:max-w-[1581px] py-[24px] grid grid-cols-2 gap-y-[24px] lg:grid-cols-4 items-center">
          <div className="flex gap-3 items-center">
            <div className="rounded-[12px] min-w-[32px] min-h-[32px] sm:min-h-[40px] md:min-h-[48px] lg:min-h-[56px] sm:min-w-[40px] md:min-w-[48px] lg:min-w-[56px] shadow-[0px_4px_12px_0px_#0000001F] bg-white flex items-center justify-center">
              <Clock
                className="size-3 sm:size-5 md:size-[24px]"
                color="#EB9757"
              />
            </div>
            <div>
              <p className="ff_poppins text-xs sm:text-sm md:text-base text-[#5C5C5C] font-medium">
                OFFER RESPONSE
              </p>
              <p className="text-base md:text-lg xl:text-xl ff_poppins font-semibold">
                {universityData?.offerTat} Days
              </p>
            </div>
          </div>

          <div className="flex gap-3 items-center">
            <div className="rounded-[12px] min-w-[32px] min-h-[32px] sm:min-h-[40px] md:min-h-[48px] lg:min-h-[56px] sm:min-w-[40px] md:min-w-[48px] lg:min-w-[56px] shadow-[0px_4px_12px_0px_#0000001F] bg-white flex items-center justify-center">
              <IndianRupee
                className="size-3 sm:size-5 md:size-[24px]"
                color="#EB9757"
              />
            </div>
            <div>
              <p className="ff_poppins text-xs sm:text-sm md:text-base text-[#5C5C5C] font-medium">
                APPLICATION FEE
              </p>
              <div className="relative">
                <p className="text-base md:text-lg xl:text-xl ff_poppins font-semibold">
                  {universityData?.applicationFees} {universityData?.currency}
                </p>
                {universityData?.currency === "GBP" && (
                  <>
                    <span className="absolute w-[80%] h-[2px] bg-red-500 top-1/2 -translate-1/2 left-[40%]"></span>
                    <span className="absolute text-red-500 top-full left-0 text-nowrap text-xs ff_poppins">
                      Fees Waived Off
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-3 items-center">
            <div className="rounded-[12px] min-w-[32px] min-h-[32px] sm:min-h-[40px] md:min-h-[48px] lg:min-h-[56px] sm:min-w-[40px] md:min-w-[48px] lg:min-w-[56px] shadow-[0px_4px_12px_0px_#0000001F] bg-white flex items-center justify-center">
              <CircleAlert
                className="size-3 sm:size-5 md:size-[24px]"
                color="#EB9757"
              />
            </div>
            <div>
              <p className="ff_poppins text-xs sm:text-sm md:text-base text-[#5C5C5C] font-medium">
                BACKLOGS
              </p>
              <p className="text-base md:text-lg xl:text-xl ff_poppins font-semibold">
                {universityData?.backlogsAllowed} backlogs accepted
              </p>
            </div>
          </div>

          <div className="flex gap-3 items-center">
            <div className="rounded-[12px] min-w-[32px] min-h-[32px] sm:min-h-[40px] md:min-h-[48px] lg:min-h-[56px] sm:min-w-[40px] md:min-w-[48px] lg:min-w-[56px] shadow-[0px_4px_12px_0px_#0000001F] bg-white flex items-center justify-center">
              <Plane
                className="size-3 sm:size-5 md:size-[24px]"
                color="#EB9757"
              />
            </div>
            <div>
              <p className="ff_poppins text-xs sm:text-sm md:text-base text-[#5C5C5C] font-medium">
                POST STUDY WORK
              </p>
              <p className="text-base md:text-lg xl:text-xl ff_poppins font-semibold">
                {postWorkPermitDuration
                  ? (/[a-zA-Z]/.test(postWorkPermitDuration.toString()) 
                      ? postWorkPermitDuration 
                      : `${postWorkPermitDuration} ${Number(postWorkPermitDuration) === 1 ? "Year" : "Years"}`)
                  : universityData?.post_course_work_permit
                  ? "Allowed"
                  : "Not allowed"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
          <div className="container 2xl:max-w-[1581px] py-3">
            <div
              ref={tabsContainerRef}
              className="flex gap-3 overflow-x-auto"
              style={{ scrollbarWidth: "none" }}
            >
              {TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    ref={(el) => {
                      tabRefs.current[tab.id] = el;
                    }}
                    onClick={() => handleTabClick(tab.id)}
                    className="relative whitespace-nowrap px-3 py-1.5 focus:outline-none ff_poppins text-base font-medium"
                  >
                    <motion.span
                      className="relative z-10"
                      animate={{ color: isActive ? "#ffffff" : "#000000" }}
                      transition={{ duration: 0.25 }}
                    >
                      {tab.label}
                    </motion.span>
                    {isActive && (
                      <motion.div
                        layoutId="tab-indicator"
                        className="absolute bottom-0 left-0 right-0 h-full w-full bg-[#021A38] rounded-[12px] z-0"
                        transition={{
                          type: "spring",
                          stiffness: 1000,
                          damping: 55,
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <div className="container 2xl:max-w-[1581px] relative z-10">
          {TABS.map((tab) => (
            <Section
              key={tab.id}
              id={tab.id}
              title={tab.label}
              sectionRef={registerRef(tab.id)}
            >
              {SECTION_CONTENT[tab.id]}
            </Section>
          ))}
          <div className="mt-[56px] bg-black flex flex-col md:flex-row items-center justify-between py-6 px-4 sm:p-7 md:p-8 lg:p-10 xl:p-[48px] rounded-[24px] mb-[120px]">
            <div>
              <p className="text-white text-2xl font-semibold">
                Ready to take the next step?
              </p>
              <p className="mt-2 text-sm sm:text-base ff_poppins text-[#FFFFFFB2]">
                Applications open for{" "}
                {formatDateWithOrdinal(uniqueOpenIntakes[0]?.deadline)} intake.{" "}
                {universityData.programCount} programs available.
              </p>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-[12px] w-full sm:w-auto mt-[24px] md:mt-0">
              <button
                onClick={expertFromHandler}
                className="text-nowrap border-[1.5px] w-full sm:w-auto border-[#FFFFFF66] rounded-full text-white font-semibold text-base ff_poppins py-[18px] px-[32px]"
              >
                Talk to Mentor
              </button>
              <Link
                href={`/all-courses/${universityData.college_slug}`}
                className="text-nowrap bg-primary-gradient w-full sm:w-auto rounded-full text-white font-semibold text-base ff_poppins py-[18px] px-[32px] text-center"
              >
                View Programs
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      {loading && loadingState && <ApiLoader />}
      {expertForm && (
        <TalkToExperts
          expertForm={expertForm}
          setExpertForm={setExpertForm}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
        />
      )}
    </div>
  );
};

export default UniversityDetail;

// =================================== OLD COMPONENTS ===================================
// "use client";
// import ApiLoader from "@/components/common/ApiLoader";
// import PerfectMatch from "@/components/tools/checkEligibility/PerfectMatches";
// import Footer from "@/components/common/Footer";
// import Pathway from "@/components/country/Pathway";
// import AllCourses from "@/components/universityDetail/AllCourses";
// import Banner from "@/components/universityDetail/Banner";
// import Campuses from "@/components/universityDetail/Campuses";
// import Details from "@/components/universityDetail/Details";
// import EntryRequirements from "@/components/universityDetail/EntryRequirements";
// import Gallery from "@/components/universityDetail/Gallery";
// import Requirement from "@/components/universityDetail/Requirement";
// import { client } from "@/sanity/client";
// import { useAppSelector } from "@/store/hooks";
// import { CountryInfo } from "@/store/types/countryTypes";
// import { University } from "@/store/types/universityTypes";
// import { formatCountryUrl } from "@/utils/formatCountryUrl";
// import { useEffect, useState } from "react";
// import Faq from "../homepage/Faq";
// import FinancialRequirements from "./FinancialRequirements";

// export const getCountryByIdQuery = `
//   *[_type == "countryData" && id == $id][0]{
//     id,
//     countryName,
//     currency,
//     description,

//     "bgImg": bgImg.asset->url,

//     popularFields[]{
//       title,
//       description,
//       icon
//     },

//     "pathway": pathway.asset->url,
//     "mobilePathway": mobilePathway.asset->url,

//     chartData{
//       lowestFee,
//       highestFee
//     },

//     earnWhileStudy,

//     slides[]{
//       title,
//       description,
//       "image": image.asset->url
//     }
//   }
// `;

// const UniversityDetail = ({
//   universityData,
// }: {
//   universityData: University;
// }) => {
//   const [loadingState, setLoadingState] = useState(true);

//   const { loading } = useAppSelector((state) => state.loader);
//   const { countryList } = useAppSelector((state) => state.country);

//   const matchedCountry = countryList.find(
//     (item) =>
//       formatCountryUrl(item.country_name) ===
//       formatCountryUrl(universityData?.addressInfo?.country || ""),
//   );
//   const countryName = matchedCountry?.country_name;
//   const [country, setCountry] = useState<CountryInfo>({
//     id: 0,
//     bgImg: "",
//     countryName: "",
//     currency: "",
//     description: "",
//     popularFields: [],
//     pathway: "",
//     mobilePathway: "",
//     chartData: {
//       lowestFee: 0,
//       highestFee: 0,
//     },
//     earnWhileStudy: [],
//     slides: [],
//   });

//   const getCountryById = async (id: number) => {
//     return await client.fetch(getCountryByIdQuery, { id });
//   };
//   useEffect(() => {
//     const fetchCountry = async () => {
//       if (!universityData.countryId) return;

//       try {
//         const data = await getCountryById(Number(universityData.countryId));
//         if (data) {
//           setCountry({
//             ...data,
//             chartData: {
//               highestFee: data.chartData?.highestFee ?? 0,
//               lowestFee: data.chartData?.lowestFee ?? 0,
//             },
//             slides:
//               data.slides?.map((slide: any) => ({
//                 ...slide,
//                 countryName: data.countryName,
//               })) || [],
//           });
//         }
//       } catch (error) {
//         console.error("Error fetching country:", error);
//       }
//     };

//     fetchCountry();
//   }, [universityData.countryId]);

//   useEffect(() => {
//     setTimeout(() => {
//       setLoadingState(false);
//     }, 2000);
//   }, []);
//   return (
//     <div className="university_page_gradient overflow-hidden">
//       <NewNavBar />
//       <Banner universityDetail={universityData} />
//       <Details data={universityData} />
//       <div className="container 2xl:max-w-[1590px] mt-[54px]">
//         <Gallery data={universityData} />
//       </div>
//       <div className="container 2xl:max-w-[1590px] mt-[54px]">
//         <EntryRequirements universityDetail={universityData} />
//       </div>
//       <Requirement data={universityData} />
//       {/* <Application /> */}
//       <div className="container 2xl:max-w-[1590px] mt-[54px]">
//         <Pathway country={country} />
//       </div>
//       {universityData.campusDetails !== null && (
//         <Campuses universityDetail={universityData} />
//       )}
//       <AllCourses universityId={universityData.college_slug || ""} />
//       <div className="container 2xl:max-w-[1580px]">
//         <div className="bg-white shadow-[0px_4px_20px_0px_#00000040] sm:rounded-[24px] mt-[45px] sm:mt-0">
//           <FinancialRequirements
//             country={universityData.addressInfo?.country || ""}
//           />
//         </div>
//       </div>
//       <div className="container 2xl:max-w-[1580px] mt-[54px] px-0 sm:px-6">
//         {/* <div className="bg-white sm:rounded-[24px] px-4 p-[1px] sm:py-4 mb-[42px]">
//           <StudentTestimonials
//             title="What Students Say"
//             description="Real stories from those who’ve experienced the difference."
//             icon={<ThumbUp />}
//           />
//         </div> */}
//       </div>
//       <Faq className="text-start pl-0" label={"university_page"} />
//       {/* <Contact /> */}
//       <Footer />
//       {loading && loadingState && <ApiLoader />}
//     </div>
//   );
// };

// export default UniversityDetail;
